import { TrackableSourceNames } from '../consts';

// Function to copy text to clipboard
const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error('Failed to copy text: ', err);
        return false;
    }
};

const updateMetaTags = (metaData: {
    url: string;
    title: string;
    description: string;
    imageUrl?: string;
}) => {
    // Update or create Open Graph URL meta tag
    let metaOgUrl = document.querySelector('meta[property="og:url"]');
    if (!metaOgUrl) {
        metaOgUrl = document.createElement('meta');
        metaOgUrl.setAttribute('property', 'og:url');
        document.head.appendChild(metaOgUrl);
    }
    metaOgUrl.setAttribute('content', metaData.url);

    // Update or create Open Graph title meta tag
    let metaOgTitle = document.querySelector('meta[property="og:title"]');
    if (!metaOgTitle) {
        metaOgTitle = document.createElement('meta');
        metaOgTitle.setAttribute('property', 'og:title');
        document.head.appendChild(metaOgTitle);
    }
    metaOgTitle.setAttribute('content', metaData.title);

    // Update or create Open Graph description meta tag
    let metaOgDesc = document.querySelector('meta[property="og:description"]');
    if (!metaOgDesc) {
        metaOgDesc = document.createElement('meta');
        metaOgDesc.setAttribute('property', 'og:description');
        document.head.appendChild(metaOgDesc);
    }
    metaOgDesc.setAttribute('content', metaData.description);

    // Update or create Open Graph image meta tag if provided
    if (metaData.imageUrl) {
        let metaOgImage = document.querySelector('meta[property="og:image"]');
        if (!metaOgImage) {
            metaOgImage = document.createElement('meta');
            metaOgImage.setAttribute('property', 'og:image');
            document.head.appendChild(metaOgImage);
        }
        metaOgImage.setAttribute('content', metaData.imageUrl);
    }
};

const openSharePopup = (url: string, name: string) => {
    const width = 600;
    const height = 400;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    window.open(
        url,
        name,
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
    );
};

export const openShareWindow = (
    platform: TrackableSourceNames,
    url: string,
    title: string,
    description: string,
    formattedText: string
) => {
    let shareUrl = '';

    switch (platform) {
        case TrackableSourceNames.FACEBOOK:
            // Update meta tags
            updateMetaTags({
                url,
                title,
                description: description,
            });

            // Focus on the clipboard approach for Facebook too
            copyToClipboard(formattedText)
                .then((success) => {
                    if (success) {
                        // Show alert like we do for LinkedIn
                        alert(
                            'The details copied to clipboard! Please paste in the Facebook share window.'
                        );
                    }

                    // Simplified URL without hashtag - just use the base URL
                    shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                    openSharePopup(shareUrl, 'facebook-share');
                })
                .catch((error) => {
                    console.error('Error copying to clipboard:', error);

                    // Fallback if clipboard fails
                    shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                    openSharePopup(shareUrl, 'facebook-share');
                });
            return;
        case TrackableSourceNames.LINKEDIN:
            // Copy formatted job details to clipboard for easy pasting
            copyToClipboard(formattedText)
                .then((success) => {
                    if (success) {
                        // Show a notification to the user (you can replace this with your app's notification system)
                        alert(
                            'The details copied to clipboard! Please paste in the LinkedIn share window.'
                        );
                    }

                    // Update meta tags as a fallback
                    updateMetaTags({
                        url,
                        title,
                        description: description,
                    });

                    // Open LinkedIn share dialog
                    shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
                    openSharePopup(shareUrl, 'linkedin-share');
                })
                .catch((error) => {
                    console.error('Error copying to clipboard:', error);

                    // Fallback to standard sharing if clipboard fails
                    updateMetaTags({
                        url,
                        title,
                        description: description,
                    });

                    shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
                    openSharePopup(shareUrl, 'linkedin-share');
                });
            return;
        case TrackableSourceNames.X:
            shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                url
            )}&text=${encodeURIComponent(description)}`;
            openSharePopup(shareUrl, 'twitter-share');
            return;
        case TrackableSourceNames.EMAIL:
            shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description}: \n\n${url}`)}`;
            window.location.href = shareUrl;
            return;
        case TrackableSourceNames.WHATSAPP:
            shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${description}: \n\n${url}`)}`;
            openSharePopup(shareUrl, 'whatsapp-share');
            return;
        default:
            return;
    }
};
